<template>
  <TooltipStandardV2
    :disabledTooltip="showText"
    arrow-position="top-center"
    :text="t(action.labelKey)"
  >
    <ButtonText
      :id="action.actionName"
      :class="['action-button', { disabled: disabled }]"
      :size="size"
      :disabled="disabled"
      :buttonText="showText ? t(action.labelKey) : undefined"
      :whiteContent="shouldInvert"
      :tabIndex="tabIndex"
      @mouseenter="hover = true"
      @mouseleave="hover = false"
      @dblclick="
        (e) => {
          e.stopPropagation()
        }
      "
      @onClicked="
        (e) => {
          $emit('action', action)
          if (stopClickPropagation) {
            e.stopPropagation()
          }
        }
      "
    >
      <template #left>
        <div class="icon" focusable="false">
          <component
            :is="action.icon"
            class="icon"
            focusable="false"
            :style="{
              color: shouldInvert ? 'white' : 'inherit'
            }"
          />
        </div>
      </template>
      <template #right>
        <ChevronToggle v-if="chevron" :state="'down'" :invert="shouldInvert" />
      </template>
    </ButtonText>
  </TooltipStandardV2>
</template>
<script setup lang="ts">
import ButtonText from '@/design-system/atoms/buttons/ButtonText.vue'
import { Action } from './types'
import TooltipStandardV2 from '@/design-system/atoms/TooltipStandardV2.vue'
import { useI18n } from 'vue-i18n'
import { computed, ref, toRefs } from 'vue'
import ChevronToggle from '@/design-system/atoms/ChevronToggle.vue'

const props = withDefaults(
  defineProps<{
    action: Action
    showText?: boolean
    size?: string
    invert?: boolean
    invertOnHover?: boolean
    chevron?: boolean
    tabIndex?: number
    disabled?: boolean
    hoverBackgroundColor?: string
    stopClickPropagation?: boolean
  }>(),
  {
    showText: false,
    invert: false,
    invertOnHover: true,
    chevron: false,
    disabled: false,
    stopClickPropagation: false,
    size: 'small-icon',
    tabIndex: 0,
    hoverBackgroundColor: '#3F46D6'
  }
)

const { hoverBackgroundColor } = toRefs(props)

defineEmits<(e: 'action', action: Action) => void>()
const { t } = useI18n()

const hover = ref(false)

const shouldInvert = computed(
  () => props.invert || (hover.value && props.invertOnHover && !props.disabled)
)
</script>

<style lang="scss" scoped>
.action-button {
  &:hover:not(.disabled) {
    background-color: v-bind(hoverBackgroundColor);
  }

  &.disabled {
    color: #767676;
  }

  .icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 16px;
    height: 16px;
  }
}
:deep(.button-base__disabled) {
  background: none;
}
</style>
